import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";

/* Public tokenised booking page for the SECOND (refill) appointment of
   the Lip Dissolving & Refill package. Design mirrors the standard CLAÈ
   booking visual language — serif heading, muted palette, minimal form.
   No pre-consent gating here: the client receives the consent email
   right after submission, exactly like a normal Lip Filler booking. */
export default function BookRefill() {
  const { pkgid } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [chosenDate, setChosenDate] = useState("");
  const [slots, setSlots] = useState([]);
  const [chosenTime, setChosenTime] = useState("");
  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    api.get(`/lip-refill/${pkgid}`, { params: { token } })
      .then((r) => setState({ loading: false, data: r.data, error: null }))
      .catch((e) => setState({ loading: false, data: null,
        error: e?.response?.data?.detail || "This refill link is no longer valid." }));
  }, [pkgid, token]);

  useEffect(() => {
    if (!chosenDate || !state.data) return;
    setSlots([]); setChosenTime("");
    api.get("/slots", { params: { date: chosenDate, duration: state.data.duration_min } })
      .then((r) => setSlots(r.data || []))
      .catch(() => setSlots([]));
  }, [chosenDate, state.data]);

  const submit = async () => {
    if (!chosenDate || !chosenTime) {
      toast.error("Please pick a date and time."); return;
    }
    setSubmitting(true);
    try {
      const r = await api.post(`/lip-refill/${pkgid}/book-refill`,
        { date: chosenDate, time: chosenTime, client_phone: phone },
        { params: { token } });
      setDone(r.data);
      toast.success("Refill appointment booked.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not complete your booking.");
    } finally {
      setSubmitting(false);
    }
  };

  if (state.loading) {
    return <main className="min-h-screen bg-[#FAF9F6]" data-testid="refill-loading" />;
  }
  if (state.error) {
    return (
      <main className="min-h-screen bg-[#FAF9F6] flex items-center justify-center px-6" data-testid="refill-error">
        <div className="max-w-md text-center">
          <h1 className="font-serif text-4xl font-light">Link not valid</h1>
          <p className="mt-4 text-stone-600">{state.error}</p>
        </div>
      </main>
    );
  }
  if (state.data?.already_booked || done) {
    return (
      <main className="min-h-screen bg-[#FAF9F6] px-6 py-16" data-testid="refill-done">
        <div className="max-w-xl mx-auto border hairline bg-white p-10">
          <p className="mono-label text-[10px]">CLAÈ · LIP DISSOLVING &amp; REFILL</p>
          <h1 className="font-serif text-4xl font-light mt-3">Refill appointment confirmed</h1>
          {done?.reference && (
            <p className="mono-label mt-6">Booking {done.reference} · {done.date} · {done.time}</p>
          )}
          <p className="mt-6 text-stone-600 text-sm">
            A consent form link will arrive in your inbox shortly — please complete
            it before your appointment. We can't wait to see you.
          </p>
        </div>
      </main>
    );
  }

  const d = state.data;
  const minDate = d.available_from || new Date().toISOString().slice(0,10);
  return (
    <main className="min-h-screen bg-[#FAF9F6] px-6 py-14" data-testid="refill-form">
      <div className="max-w-xl mx-auto border hairline bg-white p-10">
        <p className="mono-label text-[10px]">CLAÈ · LIP DISSOLVING &amp; REFILL</p>
        <h1 className="font-serif text-4xl font-light mt-3">
          Hi {(d.client_name || "there").split(" ")[0]}, book your refill
        </h1>
        <p className="mt-4 text-stone-600 text-sm">
          {d.treatment_label} · {d.duration_min} min. Available from{" "}
          <span className="font-mono">{minDate}</span>.
        </p>

        <label className="mono-label text-[10px] block mt-10">PICK A DATE</label>
        <input type="date" value={chosenDate} min={minDate}
               data-testid="refill-date"
               onChange={(e) => setChosenDate(e.target.value)}
               className="mt-2 w-full border hairline p-3 text-sm bg-white" />

        {chosenDate && (
          <>
            <label className="mono-label text-[10px] block mt-6">PICK A TIME</label>
            <div className="mt-2 grid grid-cols-3 sm:grid-cols-4 gap-2">
              {slots.length === 0 && (
                <p className="col-span-full text-sm text-stone-500 italic">
                  No slots available for this date — try another day.
                </p>
              )}
              {slots.map((s) => (
                <button key={s} type="button"
                        data-testid={`refill-slot-${s.replace(":","")}`}
                        onClick={() => setChosenTime(s)}
                        className={"border px-2 py-2 mono-label text-[11px] transition " +
                          (chosenTime === s
                            ? "border-stone-900 bg-stone-900 text-white"
                            : "border-stone-300 hover:border-stone-900")}>
                  {s}
                </button>
              ))}
            </div>
          </>
        )}

        <label className="mono-label text-[10px] block mt-6">PHONE (OPTIONAL)</label>
        <input value={phone} onChange={(e) => setPhone(e.target.value)}
               data-testid="refill-phone"
               placeholder="For appointment reminders"
               className="mt-2 w-full border hairline p-3 text-sm bg-white" />

        <button data-testid="refill-submit" disabled={submitting || !chosenDate || !chosenTime}
                onClick={submit}
                className="mt-10 w-full bg-stone-900 text-white mono-label py-4 disabled:opacity-50"
                style={{ color: "#fff" }}>
          {submitting ? "Booking…" : "Confirm Refill Appointment"}
        </button>
      </div>
    </main>
  );
}
